import { db } from "../boot/firebase";
import {
  FieldPath,
  collection,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";

/**
 * Set one entry of a delivery event's or labor's `status.loaded` map.
 *
 * Keys embed the location ID, and a temporary stop's location ID is its
 * address ("893 Eastwind Dr., Westerville, OH 43081"). A dotted string path
 * splits at the address's periods into nested maps, so the count is never read
 * back and the item cannot be checked off (sale 03500088A, 2026-10-19). A
 * FieldPath keeps the key as a single segment.
 *
 * @param {DocumentReference} ref - Event or labor document
 * @param {string} key - `${itemID}-${locationID}` with optional `@bin`
 * @param {number} count - Quantity loaded from that location
 * @param {string} uid - User making the change
 */
export function setLoadedCount(ref, key, count, uid) {
  return updateDoc(
    ref,
    new FieldPath("status", "loaded", key),
    count,
    "modified",
    new Date(),
    "modifiedBy",
    uid
  );
}

/**
 * Find delivery events whose truck-loading `loaded` map still references the
 * given sale item (keys are `${itemID}-${locationID}` with optional `@bin`).
 * Completed deliveries are excluded.
 *
 * Manually relocating such an item (RelocateItem dialog or the SaleItemEdit
 * location editor) writes `itemLocations` directly and leaves the event's
 * loaded map stale: the next unloadTruck/endLoadingTruck then throws
 * "Invalid location ID" because the units are nowhere the map claims them
 * to be (sale 01800009C incident, 2026-08-17). Callers should steer the user
 * to the delivery's Unload Truck flow instead.
 *
 * Installation events are not covered: their loaded maps live on the labor
 * docs, not the event.
 *
 * @param {string} itemID - Sale item document ID
 * @param {string} stateID - Sale state ID (`${projectID}${saleID}`)
 * @returns {Promise<Array<{id: string, title: string}>>}
 */
export async function findEventsWithLoadedItem(itemID, stateID) {
  const eventsRef = collection(db, "events");
  const [primarySnap, additionalSnap] = await Promise.all([
    getDocs(query(eventsRef, where("item", "==", stateID))),
    getDocs(
      query(eventsRef, where(`additionalSales.${stateID}.quantity`, ">", 0))
    ),
  ]);
  const seen = new Set();
  const events = [];
  for (const doc of [...primarySnap.docs, ...additionalSnap.docs]) {
    if (seen.has(doc.id)) {
      continue;
    }
    seen.add(doc.id);
    const data = doc.data();
    if (data.type !== "delivery" || data.canceled) {
      continue;
    }
    if ((data.status && data.status.type) === "delivered") {
      continue;
    }
    const loaded = (data.status && data.status.loaded) || {};
    const referenced = Object.entries(loaded).some(
      ([key, quantity]) => quantity > 0 && key.split("-")[0] === itemID
    );
    if (referenced) {
      events.push({ id: doc.id, title: data.title || doc.id });
    }
  }
  return events;
}
